import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { seo } from "@/utils/seo";
import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, Target } from "lucide-react";
import React, { useState } from "react";

// Bileşen dışına taşıyarak yeniden render edilmesini önleyebiliriz.
const initialFormData = {
  age: "under20",
  sex: "male",
  bodyWeight: "",
  height: "short",
  experienceLevel: "beginner",
  sleepQuality: "poor",
  dietQuality: "poor",
  stress: "low",
  trainingFrequency: "1x",
  recovery: "poor",
  exercise: "squat",
  trainingPhase: "hypertrophy",
  weightLifted: "",
  repsCompleted: "",
};

export const Route = createFileRoute("/_app/essay/mev-mrv-calculator")({
  component: RouteComponent,
  head: () => ({
    meta: seo({
      title: "MEV & MRV Hesaplayıcı - Antrenman Hacmi Analizi | AtalayTheCoach",
      description:
        "Kas gelişimi için ideal antrenman hacminizi hesaplayın. Minimum Etkili Hacim (MEV) ve Maksimum Toparlanabilir Hacim (MRV) analizi.",
      keywords:
        "MEV hesaplama, MRV hesaplama, antrenman hacmi, kas gelişimi, hacim rehberi, Alican Atalay",
      canonical: "https://atalaythecoach.com/essay/mev-mrv-calculator",
    }),
  }),
});

function RouteComponent() {
  const [formData, setFormData] = useState(initialFormData);
  type Results = {
    mev: number;
    mrv: number;
    details?: {
      yas?: string;
      cinsiyet?: string;
      boy?: string;
      kilo?: string;
      deneyim?: string;
      uyku?: string;
      beslenme?: string;
      toparlanma?: string;
      stres?: string;
      egzersiz?: string;
      antrenmanSikligi?: string;
      agirlik?: string;
      tekrar?: string;
      tahmini1RM?: string;
      relatifGuc?: string;
      bmi?: string;
    };
  } | null;
  const [results, setResults] = useState<Results>(null);

  const calculateAdvancedMevMrv = () => {
    let baseMev = 8;
    let baseMrv = 16;
    const ageMultipliers: Record<string, { mev: number; mrv: number }> = {
      under20: { mev: 1.1, mrv: 1.15 },
      "20-30": { mev: 1.0, mrv: 1.0 },
      "30-40": { mev: 0.95, mrv: 0.9 },
      "40+": { mev: 0.85, mrv: 0.8 },
    };
    const ageMultiplier =
      ageMultipliers[formData.age] || ageMultipliers["20-30"];
    baseMev *= ageMultiplier.mev;
    baseMrv *= ageMultiplier.mrv;
    if (formData.sex === "female") {
      baseMev *= 0.9;
      baseMrv *= 0.95;
    }
    const weight = Number.parseFloat(formData.bodyWeight);
    const heightMultipliers: Record<string, number> = {
      short_female: 1.6,
      medium: 1.7,
      tall: 1.8,
      very_tall: 1.85,
    };
    const heightM = heightMultipliers[formData.height] || 1.7;
    const bmi = weight / (heightM * heightM);
    if (bmi < 20) {
      baseMev *= 0.9;
      baseMrv *= 0.85;
    } else if (bmi > 25) {
      baseMev *= 1.05;
      baseMrv *= 1.1;
    }
    const experienceMultipliers: Record<string, { mev: number; mrv: number }> =
      {
        beginner: { mev: 0.8, mrv: 0.9 },
        intermediate: { mev: 1.0, mrv: 1.0 },
        advanced: { mev: 1.2, mrv: 1.15 },
      };
    const expMultiplier = experienceMultipliers[formData.experienceLevel];
    baseMev *= expMultiplier.mev;
    baseMrv *= expMultiplier.mrv;
    const sleepMultipliers: Record<string, { mev: number; mrv: number }> = {
      poor: { mev: 1.0, mrv: 0.7 },
      average: { mev: 1.0, mrv: 0.9 },
      good: { mev: 1.0, mrv: 1.1 },
    };
    const sleepMultiplier = sleepMultipliers[formData.sleepQuality];
    baseMrv *= sleepMultiplier.mrv;
    const dietMultipliers: Record<string, { mev: number; mrv: number }> = {
      poor: { mev: 1.1, mrv: 0.8 },
      average: { mev: 1.0, mrv: 1.0 },
      good: { mev: 0.95, mrv: 1.15 },
    };
    const dietMultiplier = dietMultipliers[formData.dietQuality];
    baseMev *= dietMultiplier.mev;
    baseMrv *= dietMultiplier.mrv;
    const recoveryMultipliers: Record<string, { mev: number; mrv: number }> = {
      poor: { mev: 1.0, mrv: 0.75 },
      below_avg: { mev: 1.0, mrv: 0.85 },
      average: { mev: 1.0, mrv: 1.0 },
      good: { mev: 1.0, mrv: 1.15 },
      excellent: { mev: 1.0, mrv: 1.25 },
    };
    const recoveryMultiplier =
      recoveryMultipliers[formData.recovery] || recoveryMultipliers.average;
    baseMrv *= recoveryMultiplier.mrv;
    const stressMultipliers: Record<string, { mev: number; mrv: number }> = {
      low: { mev: 1.0, mrv: 1.1 },
      moderate: { mev: 1.0, mrv: 1.0 },
      high: { mev: 1.0, mrv: 0.8 },
    };
    const stressMultiplier = stressMultipliers[formData.stress];
    baseMrv *= stressMultiplier.mrv;
    const exerciseMultipliers: Record<string, { mev: number; mrv: number }> = {
      squat: { mev: 1.0, mrv: 1.0 },
      bench_press: { mev: 1.1, mrv: 1.1 },
      deadlift: { mev: 0.8, mrv: 0.85 },
    };
    const exerciseMultiplier = exerciseMultipliers[formData.exercise];
    baseMev *= exerciseMultiplier.mev;
    baseMrv *= exerciseMultiplier.mrv;
    const frequencyMultipliers: Record<string, { mev: number; mrv: number }> = {
      "1x": { mev: 0.9, mrv: 0.9 },
      "1-2x": { mev: 1.0, mrv: 1.0 },
      "2x": { mev: 1.1, mrv: 1.1 },
      "2-3x": { mev: 1.15, mrv: 1.15 },
      "3x+": { mev: 1.2, mrv: 1.2 },
    };
    const freqMultiplier = frequencyMultipliers[formData.trainingFrequency];
    baseMev *= freqMultiplier.mev;
    baseMrv *= freqMultiplier.mrv;
    const weight_kg = Number.parseFloat(formData.weightLifted);
    const reps = Number.parseFloat(formData.repsCompleted);
    let estimated1RM = 0;
    let relativeStrength = 0;
    if (weight_kg && reps) {
      estimated1RM = weight_kg * (1 + reps / 30);
      relativeStrength = estimated1RM / weight;
      if (relativeStrength > 1.5) {
        baseMev *= 1.1;
        baseMrv *= 1.15;
      } else if (relativeStrength < 1.0) {
        baseMev *= 0.9;
        baseMrv *= 0.9;
      }
    }
    const finalMev = Math.max(6, Math.round(baseMev));
    return {
      mev: finalMev,
      mrv: Math.max(finalMev + 4, Math.round(baseMrv)),
      details: {
        yas: (() => {
          switch (formData.age) {
            case "under20":
              return "18";
            case "20-30":
              return "25";
            case "30-40":
              return "35";
            case "40+":
              return "45";
            default:
              return formData.age;
          }
        })(),
        cinsiyet: formData.sex,
        boy: (() => {
          switch (formData.height) {
            case "short":
              return "160";
            case "medium":
              return "170";
            case "tall":
              return "180";
            case "very_tall":
              return "185";
            default:
              return formData.height;
          }
        })(),
        kilo: formData.bodyWeight,
        deneyim: formData.experienceLevel,
        uyku: formData.sleepQuality,
        beslenme: formData.dietQuality,
        toparlanma: formData.recovery,
        stres: formData.stress,
        egzersiz: formData.exercise,
        antrenmanSikligi: formData.trainingFrequency,
        agirlik: formData.weightLifted,
        tekrar: formData.repsCompleted,
        tahmini1RM: estimated1RM ? estimated1RM.toFixed(1) : undefined,
        relatifGuc: relativeStrength ? relativeStrength.toFixed(2) : undefined,
        bmi: bmi ? bmi.toFixed(1) : undefined,
      },
    };
  };
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (
      !formData.bodyWeight ||
      !formData.weightLifted ||
      !formData.repsCompleted
    ) {
      setError(
        "Lütfen Kilo, Kaldırılan Ağırlık ve Tekrar Sayısı alanlarını doldurun.",
      );
      return;
    }
    const result = calculateAdvancedMevMrv();
    setResults(result);
    setTimeout(() => {
      const resultBox = document.getElementById("mev-mrv-result");
      if (resultBox) {
        resultBox.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 200);
  };
  // ...bozuk/fazla fonksiyon kapanışları ve return silindi...

  // Başa dön ve formu sıfırla
  const handleReset = () => {
    setFormData(initialFormData);
    setResults(null);
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="my-16 min-h-screen bg-background px-2 pt-6 pb-16 sm:px-4 md:my-24 md:px-8 md:pt-10 md:pb-20">
      <div className="mx-auto max-w-full md:max-w-3xl">
        {/* --- GİRİŞ BÖLÜMÜ --- */}
        <div className="mb-8 text-center md:mb-12">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-primary/10 p-4 dark:bg-primary/20">
              <Target className="h-12 w-12 text-primary" />
            </div>
          </div>
          <h1 className="mb-6 font-extrabold font-serif text-2xl text-foreground leading-tight tracking-tight md:text-4xl lg:text-5xl">
            Antrenman Hacminizi Keşfedin: MEV & MRV
          </h1>
          <p className="mx-auto max-w-3xl text-base text-muted-foreground leading-relaxed md:text-lg">
            Kas gelişimi için ne kadar antrenman yapmanız gerektiğini hiç merak
            ettiniz mi? Bu interaktif rehber, sizin için en uygun antrenman
            hacmini bulmanıza yardımcı olacak. "Minimum Etkili Hacim" (MEV) ve
            "Maksimum Toparlanabilir Hacim" (MRV) kavramlarını anlamak,
            platoları aşmanızın ve hedeflerinize ulaşmanızın anahtarıdır.
          </p>
        </div>

        <Alert
          variant="destructive"
          className="mb-8 border-yellow-500/50 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400"
        >
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="font-medium">
            Bu hesaplayıcı şu anda pasif durumdadır. Mevcut hacim formülleri
            bireysel egzersiz bazında kafa karışıklığı yaratabildiği için
            (örneğin bacak hacmi vs. tekil egzersiz seti), daha doğru ve
            anlaşılır bir formülasyonla güncellenmek üzere geçici olarak devre
            dışı bırakılmıştır. Yakında daha iyi bir versiyonla tekrar aktif
            edilecektir.
          </AlertDescription>
        </Alert>

        <form onSubmit={handleSubmit} autoComplete="off">
          {/* --- KİŞİSEL BİLGİLER --- */}
          <div className="mb-8 md:mb-12">
            <h2 className="mb-2 font-bold font-serif text-foreground text-xl md:mb-3 md:text-2xl">
              Sizi Tanıyalım
            </h2>
            <p className="mb-4 text-muted-foreground text-sm leading-relaxed md:mb-6 md:text-base">
              Yaşınız, cinsiyetiniz ve vücut ölçüleriniz, antrenman hacminizi
              doğrudan etkiler. Lütfen aşağıdaki bilgileri doldurun.
            </p>
            <Card>
              <CardContent className="space-y-3 md:space-y-4">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4">
                  <div className="space-y-1 md:space-y-2">
                    <Label htmlFor="age">Yaş</Label>
                    <Select
                      onValueChange={(v) => handleSelectChange("age", v)}
                      defaultValue={formData.age}
                    >
                      <SelectTrigger className="h-10 border border-input bg-background text-foreground text-sm shadow-sm focus:ring-2 focus:ring-primary/40 md:h-12 md:text-base">
                        <SelectValue placeholder="Yaş seçin" />
                      </SelectTrigger>
                      <SelectContent className="border border-border bg-background text-foreground text-sm md:text-base">
                        <SelectItem value="under20" className="text-foreground">
                          20 yaş altı
                        </SelectItem>
                        <SelectItem value="20-30" className="text-foreground">
                          20-30
                        </SelectItem>
                        <SelectItem value="30-40" className="text-foreground">
                          30-40
                        </SelectItem>
                        <SelectItem value="40+" className="text-foreground">
                          40+
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sex">Cinsiyet</Label>
                    <Select
                      onValueChange={(v) => handleSelectChange("sex", v)}
                      defaultValue={formData.sex}
                    >
                      <SelectTrigger className="border border-input bg-background text-foreground shadow-sm focus:ring-2 focus:ring-primary/40">
                        <SelectValue placeholder="Cinsiyet seçin" />
                      </SelectTrigger>
                      <SelectContent className="border border-border bg-background text-foreground">
                        <SelectItem value="male" className="text-foreground">
                          Erkek
                        </SelectItem>
                        <SelectItem value="female" className="text-foreground">
                          Kadın
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="height">Boy</Label>
                  <Select
                    onValueChange={(v) => handleSelectChange("height", v)}
                    defaultValue={formData.height}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Boy seçin (cm)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="short_female">
                        Kısa (150-165 cm)
                      </SelectItem>
                      <SelectItem value="medium">Orta (166-175 cm)</SelectItem>
                      <SelectItem value="tall">Uzun (176-185 cm)</SelectItem>
                      <SelectItem value="very_tall">
                        Çok Uzun (186 cm ve üzeri)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bodyWeight">Vücut Ağırlığı</Label>
                  <Input
                    id="bodyWeight"
                    name="bodyWeight"
                    type="number"
                    placeholder="örn. 70"
                    value={formData.bodyWeight}
                    onChange={handleChange}
                    min={30}
                    max={250}
                    className="border border-input bg-background text-foreground shadow-sm focus:ring-2 focus:ring-primary/40"
                  />
                  <p className="text-muted-foreground text-xs">
                    Lütfen kilogram (kg) cinsinden giriniz. Bu alan,
                    hesaplamanın temel taşlarından biridir.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
          {/* --- YAŞAM TARZI & DENEYİM --- */}
          <div className="mb-12">
            <h2 className="mb-3 font-bold font-serif text-2xl text-foreground">
              Deneyim ve Yaşam Tarzı
            </h2>
            <p className="mb-6 text-muted-foreground leading-relaxed">
              Ne kadar süredir antrenman yaptığınız ve günlük yaşam
              alışkanlıklarınız, ne kadar hacimden toparlanabileceğinizi
              belirler. Uyku, beslenme ve stres yönetimi, en az antrenmanın
              kendisi kadar önemlidir.
            </p>
            <Card>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="experienceLevel">Deneyim Seviyesi</Label>
                    <Select
                      onValueChange={(v) =>
                        handleSelectChange("experienceLevel", v)
                      }
                      defaultValue={formData.experienceLevel}
                    >
                      <SelectTrigger className="border border-input bg-background text-foreground shadow-sm focus:ring-2 focus:ring-primary/40">
                        <SelectValue placeholder="Seviye seçin" />
                      </SelectTrigger>
                      <SelectContent className="border border-border bg-background text-foreground">
                        <SelectItem
                          value="beginner"
                          className="text-foreground"
                        >
                          Başlangıç (&lt;1 yıl)
                        </SelectItem>
                        <SelectItem
                          value="intermediate"
                          className="text-foreground"
                        >
                          Orta (1-3 yıl)
                        </SelectItem>
                        <SelectItem
                          value="advanced"
                          className="text-foreground"
                        >
                          İleri (3+ yıl)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dietQuality">Beslenme Kalitesi</Label>
                    <Select
                      onValueChange={(v) =>
                        handleSelectChange("dietQuality", v)
                      }
                      defaultValue={formData.dietQuality}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Beslenme seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="poor">
                          Kötü (yetersiz kalori)
                        </SelectItem>
                        <SelectItem value="average">
                          Orta (kalori yeterli, makrolara odak yok)
                        </SelectItem>
                        <SelectItem value="good">
                          İyi (dengeli makro ve zamanlama)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="recovery">Toparlanma Kalitesi</Label>
                    <Select
                      onValueChange={(v) => handleSelectChange("recovery", v)}
                      defaultValue={formData.recovery}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Toparlanma seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="poor">
                          Kötü toparlanma (günlerce ağrı)
                        </SelectItem>
                        <SelectItem value="below_avg">
                          Ortalama altı toparlanma (2 gün ağrı)
                        </SelectItem>
                        <SelectItem value="average">
                          Ortalama toparlanma (1-2 gün ağrı)
                        </SelectItem>
                        <SelectItem value="good">
                          İyi toparlanma (nadiren ağrı, yüksek tolerans)
                        </SelectItem>
                        <SelectItem value="excellent">
                          Mükemmel toparlanma (nadiren ağrı, yüksek hacme
                          tolerans)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sleepQuality">Uyku Kalitesi</Label>
                    <Select
                      onValueChange={(v) =>
                        handleSelectChange("sleepQuality", v)
                      }
                      defaultValue={formData.sleepQuality}
                    >
                      <SelectTrigger className="border border-input bg-background text-foreground shadow-sm focus:ring-2 focus:ring-primary/40">
                        <SelectValue placeholder="Uyku kalitesi seçin" />
                      </SelectTrigger>
                      <SelectContent className="border border-border bg-background text-foreground">
                        <SelectItem value="poor" className="text-foreground">
                          Kötü (&lt;5 saat)
                        </SelectItem>
                        <SelectItem value="average" className="text-foreground">
                          Orta (6-7 saat)
                        </SelectItem>
                        <SelectItem value="good" className="text-foreground">
                          İyi (8+ saat)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stress">Antrenman Dışı Stres</Label>
                    <Select
                      onValueChange={(v) => handleSelectChange("stress", v)}
                      defaultValue={formData.stress}
                    >
                      <SelectTrigger className="border border-input bg-background text-foreground shadow-sm focus:ring-2 focus:ring-primary/40">
                        <SelectValue placeholder="Stres seviyesi seçin" />
                      </SelectTrigger>
                      <SelectContent className="border border-border bg-background text-foreground">
                        <SelectItem value="low" className="text-foreground">
                          Düşük
                        </SelectItem>
                        <SelectItem
                          value="moderate"
                          className="text-foreground"
                        >
                          Orta
                        </SelectItem>
                        <SelectItem value="high" className="text-foreground">
                          Yüksek
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          {/* --- ANTRENMAN DETAYLARI --- */}
          <div className="mb-12">
            <h2 className="mb-3 font-bold font-serif text-2xl text-foreground">
              Antrenman Detayları
            </h2>
            <p className="mb-6 text-muted-foreground leading-relaxed">
              Hangi egzersizi yaptığınız, kaldırdığınız ağırlık ve tekrar
              sayısı, antrenman hacminizi belirlemede önemlidir.
            </p>
            <Card>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="exercise">Egzersiz</Label>
                    <Select
                      onValueChange={(v) => handleSelectChange("exercise", v)}
                      defaultValue={formData.exercise}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Egzersiz seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="squat" className="text-foreground">
                          Squat
                        </SelectItem>
                        <SelectItem
                          value="bench_press"
                          className="text-foreground"
                        >
                          Bench Press
                        </SelectItem>
                        <SelectItem
                          value="deadlift"
                          className="text-foreground"
                        >
                          Deadlift
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="trainingFrequency">
                      Haftalık Antrenman Sıklığı
                    </Label>
                    <Select
                      onValueChange={(v) =>
                        handleSelectChange("trainingFrequency", v)
                      }
                      defaultValue={formData.trainingFrequency}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sıklık seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1x">Haftada 1 kez</SelectItem>
                        <SelectItem value="1-2x">Haftada 1-2 kez</SelectItem>
                        <SelectItem value="2x">Haftada 2 kez</SelectItem>
                        <SelectItem value="2-3x">Haftada 2-3 kez</SelectItem>
                        <SelectItem value="3x+">Haftada 3+ kez</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="weightLifted">
                      Kaldırılan Ağırlık (kg)
                    </Label>
                    <Input
                      id="weightLifted"
                      name="weightLifted"
                      type="number"
                      placeholder="örn. 80"
                      value={formData.weightLifted}
                      onChange={handleChange}
                      className="border border-input bg-background text-foreground shadow-sm focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="repsCompleted">Tekrar Sayısı</Label>
                    <Input
                      id="repsCompleted"
                      name="repsCompleted"
                      type="number"
                      placeholder="örn. 5"
                      value={formData.repsCompleted}
                      onChange={handleChange}
                      className="border border-input bg-background text-foreground shadow-sm focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="mt-8 flex justify-center">
            <Button
              type="submit"
              size="lg"
              className="cursor-not-allowed px-8 py-4 font-bold text-lg opacity-50"
              disabled
            >
              Hesaplayıcı Pasif
            </Button>
          </div>
          {error && (
            <div className="flex ">
              <Alert variant="destructive" className="m-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}
        </form>
        {results && (
          <div>
            <h2 className="mb-3 flex justify-center py-8 font-extrabold font-serif text-4xl text-foreground drop-shadow-sm">
              İşte Sonuçlarınız!
            </h2>

            <Card className="mx-auto max-w-full md:max-w-2xl">
              <CardContent className="p-4 md:p-8">
                <div className="flex flex-col justify-around gap-4 md:flex-row md:gap-6">
                  <div className="flex-1 rounded-lg bg-white/90 p-3 text-center md:p-4 dark:bg-background/80">
                    <p className="mb-1 text-base text-muted-foreground md:mb-2 md:text-lg">
                      Minimum Etkili Hacim (MEV)
                    </p>
                    <p className="font-extrabold text-3xl text-primary drop-shadow md:text-5xl">
                      {results.mev}
                    </p>
                    <p className="mt-1 text-muted-foreground text-xs md:text-sm">
                      set/hafta
                    </p>
                  </div>
                  <div className="flex-1 rounded-lg bg-white/90 p-3 text-center md:p-4 dark:bg-background/80">
                    <p className="mb-1 text-base text-muted-foreground md:mb-2 md:text-lg">
                      Maksimum Toparlanabilir Hacim (MRV)
                    </p>
                    <p className="font-extrabold text-3xl text-primary drop-shadow md:text-5xl">
                      {results.mrv}
                    </p>
                    <p className="mt-1 text-muted-foreground text-xs md:text-sm">
                      set/hafta
                    </p>
                  </div>
                </div>
                <div className="mt-6 text-center text-muted-foreground md:mt-8">
                  <p className="mb-3 text-xs md:mb-4 md:text-base">
                    <strong>MEV:</strong> Kas gelişimini sürdürmek için yapmanız
                    gereken minimum set sayısıdır.
                    <br />
                    <strong>MRV:</strong> Vücudunuzun bir hafta içinde
                    toparlanabileceği maksimum set sayısıdır. Bu sınırı aşmak,
                    aşırı antrenmana yol açabilir.
                  </p>
                  <Button
                    onClick={handleReset}
                    variant="outline"
                    className="mt-2 px-4 py-2 text-xs md:px-8 md:py-4 md:text-base"
                  >
                    Yeniden Hesapla
                  </Button>
                </div>
                <div className="mt-6 md:mt-8">
                  <h3 className="mb-1 font-bold text-base md:mb-2 md:text-lg">
                    Hesaplamaya Etki Eden Faktörler
                  </h3>
                  <div className="grid grid-cols-1 gap-1 text-xs md:grid-cols-2 md:gap-2 md:text-sm">
                    <div>
                      <b>Yaş:</b> {results.details?.yas}
                    </div>
                    <div>
                      <b>Cinsiyet:</b>{" "}
                      {results.details?.cinsiyet === "male" ? "Erkek" : "Kadın"}
                    </div>
                    <div>
                      <b>Boy:</b> {results.details?.boy} cm
                    </div>
                    <div>
                      <b>Kilo:</b> {results.details?.kilo} kg
                    </div>
                    <div>
                      <b>Deneyim:</b> {results.details?.deneyim}
                    </div>
                    <div>
                      <b>Uyku:</b> {results.details?.uyku}
                    </div>
                    <div>
                      <b>Beslenme:</b> {results.details?.beslenme}
                    </div>
                    <div>
                      <b>Toparlanma:</b> {results.details?.toparlanma}
                    </div>
                    <div>
                      <b>Stres:</b> {results.details?.stres}
                    </div>
                    <div>
                      <b>Egzersiz:</b> {results.details?.egzersiz}
                    </div>
                    <div>
                      <b>Antrenman Sıklığı:</b>{" "}
                      {results.details?.antrenmanSikligi}
                    </div>
                    <div>
                      <b>Kaldırılan Ağırlık:</b> {results.details?.agirlik} kg
                    </div>
                    <div>
                      <b>Tekrar:</b> {results.details?.tekrar}
                    </div>
                    <div>
                      <b>Tahmini 1RM:</b>{" "}
                      {results.details?.tahmini1RM
                        ? `${results.details.tahmini1RM} kg`
                        : "-"}
                    </div>
                    <div>
                      <b>BMI:</b> {results.details?.bmi}
                    </div>
                    <div>
                      <b>Relatif Güç:</b> {results.details?.relatifGuc}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
